import { ArrowBack, LocationOnOutlined } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneNumberField from "../../../../components/common/PhoneNumberField";
import CustomTextField from "../../../../components/common/Textfield";
import apiService from "../../../../service/Api";
import LocationPicker from "./LocationPicker";
import FuneralPreviewCard from "./FuneralPreviewCard";

interface QuoteResponse {
  id: number;
  contact?: {
    id: number;
    first_name: string;
    last_name: string;
    phone: string;
  };
  deceased?: {
    first_name: string;
    surname: string;
    nickname?: string;
    date_of_birth?: string;
    date_of_death?: string;
    person_image?: string;
  } | null;
  event_datetime: string;
  venue_name: string;
  venue_latitude: string;
  venue_longitude: string;
  created_at: string;
}

const QuoteRequestPreview = () => {
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Editable form data
  const [editData, setEditData] = useState({
    eventDate: "",
    eventTime: "",
    venueName: "",
    belovedFirstName: "",
    belovedSurname: "",
    clientFirstName: "",
    clientLastName: "",
    clientPhone: "",
  });

  const navigate = useNavigate();

  const { id } = useParams();
  const [whereAnchor, setWhereAnchor] = React.useState<HTMLElement | null>(
    null,
  );

  const handleLocationClick = (e: React.MouseEvent<HTMLElement>) => {
    setWhereAnchor(e.currentTarget);
  };

  const handleLocationClose = () => {
    setWhereAnchor(null);
  };

  const handleLocationChange = (location: string) => {
    setEditData((prev) => ({ ...prev, venueName: location }));
    setWhereAnchor(null);
  };

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await apiService.get<QuoteResponse>(
          `/api/bookings/quote-request/${id}/`,
        );
        setQuote(response.data);

        // Initialize edit data
        const eventDate = response.data.event_datetime
          ? response.data.event_datetime.split("T")[0]
          : "";
        const eventTime = response.data.event_datetime
          ? response.data.event_datetime.split("T")[1]?.slice(0, 5)
          : "";

        setEditData({
          eventDate,
          eventTime,
          venueName: response.data.venue_name || "",
          belovedFirstName: response.data.deceased?.first_name || "",
          belovedSurname: response.data.deceased?.surname || "",
          clientFirstName: response.data.contact?.first_name || "",
          clientLastName: response.data.contact?.last_name || "",
          clientPhone: response.data.contact?.phone || "",
        });
      } catch (err) {
        console.error("Failed to fetch quote", err);
        toast.error("Failed to load quote details");
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  const handleReset = () => {
    // Reset to original data
    if (quote) {
      const eventDate = quote.event_datetime
        ? quote.event_datetime.split("T")[0]
        : "";
      const eventTime = quote.event_datetime
        ? quote.event_datetime.split("T")[1]?.slice(0, 5)
        : "";

      setEditData({
        eventDate,
        eventTime,
        venueName: quote.venue_name || "",
        belovedFirstName: quote.deceased?.first_name || "",
        belovedSurname: quote.deceased?.surname || "",
        clientFirstName: quote.contact?.first_name || "",
        clientLastName: quote.contact?.last_name || "",
        clientPhone: quote.contact?.phone || "",
      });
    }
  };

  const handleSave = async () => {
    if (!quote) return;

    // Validation
    if (!editData.eventDate || !editData.eventTime || !editData.venueName) {
      toast.error("Please fill in all event details");
      return;
    }

    setIsSaving(true);

    try {
      const eventDatetime = `${editData.eventDate}T${editData.eventTime}:00`;

      const payload = {
        event_datetime: eventDatetime,
        venue_name: editData.venueName,
        venue_latitude: quote.venue_latitude,
        venue_longitude: quote.venue_longitude,
      };

      const response = await apiService.patch<QuoteResponse>(
        `/api/bookings/quote-request/${id}/`,
        payload,
      );

      setQuote(response.data);

      const newEventDate = response.data.event_datetime
        ? response.data.event_datetime.split("T")[0]
        : "";
      const newEventTime = response.data.event_datetime
        ? response.data.event_datetime.split("T")[1]?.slice(0, 5)
        : "";

      setEditData({
        eventDate: newEventDate,
        eventTime: newEventTime,
        venueName: response.data.venue_name || "",
        belovedFirstName:
          response.data.deceased?.first_name || editData.belovedFirstName,
        belovedSurname:
          response.data.deceased?.surname || editData.belovedSurname,
        clientFirstName:
          response.data.contact?.first_name || editData.clientFirstName,
        clientLastName:
          response.data.contact?.last_name || editData.clientLastName,
        clientPhone: response.data.contact?.phone || editData.clientPhone,
      });

      toast.success("Quote updated successfully!");
    } catch (err: unknown) {
      console.error("Failed to update quote", err);
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to update quote",
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 2,
        }}
      >
        <CircularProgress size={50} thickness={4} sx={{ color: "#614338" }} />
        <Typography variant="h6" color="text.secondary">
          Loading quote details...
        </Typography>
      </Box>
    );
  }

  if (!quote) {
    return (
      <Typography color="error">
        Something went wrong. Please try again later.
      </Typography>
    );
  }

  const belovedName =
    `${editData.belovedFirstName} ${editData.belovedSurname}`.trim() || "N/A";
  const clientName =
    `${editData.clientFirstName} ${editData.clientLastName}`.trim() || "N/A";

  const formatDate = (dateString: string) => {
    if (!dateString) return "Add date";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <Box
          onClick={() => navigate(-1)}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        >
          <IconButton sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">Quote Preview</Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={isSaving}
              sx={{ borderColor: "grey.400", color: "text.primary" }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                bgcolor: "#614338",
                "&:hover": { bgcolor: "#4a3329" },
              }}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 0 }}>
        <Box sx={{ background: "#614338", color: "#fff", p: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Quote Request Details
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Submitted on {new Date(quote.created_at).toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            ID: {quote.id}
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Event Information */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Event Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <CustomTextField
                fullWidth
                label="Event Date"
                type="date"
                value={editData.eventDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditData((prev) => ({
                    ...prev,
                    eventDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <CustomTextField
                fullWidth
                label="Start Time"
                type="time"
                value={editData.eventTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditData((prev) => ({
                    ...prev,
                    eventTime: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <CustomTextField
                fullWidth
                label="Location"
                value={editData.venueName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditData((prev) => ({
                    ...prev,
                    venueName: e.target.value,
                  }))
                }
                InputProps={{
                  endAdornment: (
                    <LocationOnOutlined
                      sx={{
                        color: "text.secondary",
                        mr: 0,
                        ml: 1,
                      }}
                    />
                  ),
                }}
                onClick={handleLocationClick}
              />
            </Grid>

            {/* Beloved Information */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Beloved Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <CustomTextField
                fullWidth
                label="First Name"
                value={editData.belovedFirstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditData((prev) => ({
                    ...prev,
                    belovedFirstName: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />

              <CustomTextField
                fullWidth
                label="Surname"
                value={editData.belovedSurname}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditData((prev) => ({
                    ...prev,
                    belovedSurname: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />

              {quote.deceased && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    src={quote.deceased.person_image}
                    alt={belovedName}
                    sx={{ width: 64, height: 64 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {belovedName}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>

            {/* Client Information */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Client Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <CustomTextField
                fullWidth
                label="First Name"
                value={editData.clientFirstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditData((prev) => ({
                    ...prev,
                    clientFirstName: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />

              <CustomTextField
                fullWidth
                label="Last Name"
                value={editData.clientLastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditData((prev) => ({
                    ...prev,
                    clientLastName: e.target.value,
                  }))
                }
                sx={{ mb: 2 }}
              />

              <PhoneNumberField
                label="Phone Number"
                value={editData.clientPhone}
                onChange={(value: string) =>
                  setEditData((prev) => ({ ...prev, clientPhone: value }))
                }
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, bgcolor: "#f9fafb", borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Complete Information Summary
            </Typography>

            <Grid container spacing={4}>
              {/* Summary Grid */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Request ID:</strong>
                    </Typography>
                    <Typography>{quote.id}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Submitted:</strong>
                    </Typography>
                    <Typography>
                      {new Date(quote.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Client Name:</strong>
                    </Typography>
                    <Typography>{clientName}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Client Phone:</strong>
                    </Typography>
                    <Typography>{editData.clientPhone}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Event Date:</strong>
                    </Typography>
                    <Typography>{editData.eventDate}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Event Time:</strong>
                    </Typography>
                    <Typography>{editData.eventTime}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Location:</strong>
                    </Typography>
                    <Typography>{editData.venueName}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography>
                      <strong>Coordinates:</strong>
                    </Typography>
                    <Typography>
                      {quote.venue_latitude}, {quote.venue_longitude}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Funeral Preview Card */}
              <Grid
                size={{ xs: 12, sm: 6 }}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <FuneralPreviewCard
                  deceasedName={belovedName || "Beloved Name"}
                  birthDate={quote.deceased?.date_of_birth || ""}
                  deathDate={quote.deceased?.date_of_death || ""}
                  serviceDate={formatDate(editData.eventDate)}
                  serviceTime={editData.eventTime}
                  timeZone="CAT"
                  location={editData.venueName}
                  isLiveStream
                  broadcastBy="MUZUKURU"
                  //circleImageUrl={editData.photo || ""}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      <LocationPicker
        open={Boolean(whereAnchor)}
        anchorEl={whereAnchor}
        onClose={handleLocationClose}
        value={editData.venueName}
        onChange={handleLocationChange}
      />
    </Box>
  );
};

export default QuoteRequestPreview;
